import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export const ALLOWED_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "csv", "txt", "doc", "docx", "xls", "xlsx"];
export const MAX_UPLOAD = 500 * 1024 * 1024;

const validFiles = (files) => {
  const valid = [];
  for (const f of files) {
    const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXTS.includes(ext)) { toast.error(`"${f.name}": type ".${ext || "?"}" not allowed`); continue; }
    if (f.size > MAX_UPLOAD) { toast.error(`"${f.name}" exceeds the 500 MB limit`); continue; }
    valid.push(f);
  }
  return valid;
};

export function useAttachmentUpload({ taskId, onUploaded, onBatchDone }) {
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [batch, setBatch] = useState(null);

  const uploadOne = async (file) => {
    const { data: init } = await api.post(`/tasks/${taskId}/attachments/init`,
      { filename: file.name, size: file.size, content_type: file.type });
    const { upload_id, chunk_size } = init;
    const CH = chunk_size || 8 * 1024 * 1024;
    for (let start = 0; start < file.size; start += CH) {
      const chunk = file.slice(start, Math.min(start + CH, file.size));
      await api.put(`/tasks/${taskId}/attachments/chunk/${upload_id}`, chunk,
        { headers: { "Content-Type": "application/octet-stream" } });
      setUploadPct(Math.min(99, Math.round((Math.min(start + CH, file.size) / file.size) * 100)));
    }
    const { data: att } = await api.post(`/tasks/${taskId}/attachments/complete/${upload_id}`);
    onUploaded(att);
  };

  const uploadMany = async (files) => {
    if (uploading || !files.length) return;
    const valid = validFiles(files);
    if (!valid.length) return;
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < valid.length; i++) {
      setBatch({ current: i + 1, total: valid.length });
      setUploadPct(0);
      try { await uploadOne(valid[i]); ok += 1; }
      catch (err) { toast.error(`"${valid[i].name}": ${apiErr(err, "upload failed")}`); }
    }
    if (ok) { toast.success(ok > 1 ? `${ok} files uploaded` : "File uploaded"); onBatchDone(); }
    setUploading(false); setUploadPct(0); setBatch(null);
  };

  return { uploading, uploadPct, batch, uploadMany };
}
